import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Edit3, Trash2, Check, X,
  Eye, Loader2, ToggleLeft, ToggleRight
} from 'lucide-react';

export default function PromptsPage() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/prompts');
      setPrompts(data);
    } catch (err) {
      toast.error('Error al cargar prompts');
    }
    setLoading(false);
  };

  const createPrompt = async (e) => {
    e.preventDefault();
    if (!formName || !formContent) {
      toast.error('Completa todos los campos');
      return;
    }
    try {
      await api.post('/api/prompts', { name: formName, content: formContent });
      toast.success('Prompt creado');
      setShowCreateForm(false);
      setFormName('');
      setFormContent('');
      loadPrompts();
    } catch (err) {
      toast.error('Error al crear prompt');
    }
  };

  const startEdit = (prompt) => {
    setEditingId(prompt.id);
    setFormName(prompt.name);
    setFormContent(prompt.content);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/api/prompts/${editingId}`, { name: formName, content: formContent });
      toast.success('Prompt actualizado');
      setEditingId(null);
      loadPrompts();
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  const toggleActive = async (id, currentActive) => {
    try {
      await api.put(`/api/prompts/${id}`, { active: !currentActive });
      toast.success(!currentActive ? 'Prompt activado' : 'Prompt desactivado');
      loadPrompts();
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  const deletePrompt = async (id) => {
    if (!window.confirm('¿Eliminar este prompt?')) return;
    try {
      await api.del(`/api/prompts/${id}`);
      toast.success('Prompt eliminado');
      loadPrompts();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="prompts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Gestión de Prompts
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Configura los prompts del sistema para el análisis IA
          </p>
        </div>
        <button
          onClick={() => { setShowCreateForm(!showCreateForm); setEditingId(null); }}
          data-testid="create-prompt-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-lg"
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          <Plus size={16} />
          Nuevo Prompt
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div
          className="rounded-xl border p-6 mb-6"
          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
          data-testid="create-prompt-form"
        >
          <h3 className="font-heading font-semibold text-base mb-4" style={{ color: 'var(--text-primary)' }}>
            Nuevo Prompt del Sistema
          </h3>
          <form onSubmit={createPrompt} className="space-y-4">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nombre</label>
              <input
                type="text"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                data-testid="prompt-name-input"
                placeholder="Ej: Prompt de Ventas Seguros"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Contenido</label>
              <textarea
                value={formContent}
                onChange={e => setFormContent(e.target.value)}
                data-testid="prompt-content-input"
                rows={10}
                placeholder="Escribe el prompt del sistema aquí..."
                className="w-full px-4 py-3 rounded-lg border text-sm outline-none font-mono resize-y"
                style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" data-testid="save-prompt-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                <Check size={16} />
                Guardar
              </button>
              <button type="button" onClick={() => setShowCreateForm(false)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>
                <X size={16} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Prompts List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : prompts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" style={{ color: 'var(--text-muted)' }}>
          <MessageSquare size={48} className="mb-4 opacity-30" />
          <p className="text-lg font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sin prompts</p>
          <p className="text-sm">Crea un prompt del sistema para comenzar</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prompts.map(prompt => (
            <div
              key={prompt.id}
              className="rounded-xl border p-5 transition-all"
              style={{ backgroundColor: 'var(--surface-primary)', borderColor: prompt.active ? 'var(--accent-primary)' : 'var(--border-color)' }}
              data-testid={`prompt-item-${prompt.id}`}
            >
              {editingId === prompt.id ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={formName}
                    onChange={e => setFormName(e.target.value)}
                    data-testid="edit-prompt-name"
                    className="w-full px-4 py-2 rounded-lg border text-sm outline-none"
                    style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                  <textarea
                    value={formContent}
                    onChange={e => setFormContent(e.target.value)}
                    data-testid="edit-prompt-content"
                    rows={8}
                    className="w-full px-4 py-2 rounded-lg border text-sm outline-none font-mono resize-y"
                    style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} data-testid="save-edit-btn" className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                      <Check size={14} /> Guardar
                    </button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}>
                      <X size={14} /> Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                          {prompt.name}
                        </h3>
                        {prompt.active && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-green-500/20 text-green-400">
                            Activo
                          </span>
                        )}
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-muted)' }}>
                          v{prompt.version}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {new Date(prompt.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      {previewId === prompt.id && (
                        <pre className="mt-3 text-xs font-mono leading-relaxed p-4 rounded-lg overflow-auto max-h-64" style={{ backgroundColor: 'var(--background)', color: 'var(--text-secondary)' }}>
                          {prompt.content}
                        </pre>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPreviewId(previewId === prompt.id ? null : prompt.id)} data-testid={`preview-prompt-${prompt.id}`} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }}>
                        <Eye size={14} />
                      </button>
                      <button onClick={() => toggleActive(prompt.id, prompt.active)} data-testid={`toggle-prompt-${prompt.id}`} className="p-2 rounded-lg">
                        {prompt.active ? <ToggleRight size={20} className="text-green-500" /> : <ToggleLeft size={20} style={{ color: 'var(--text-muted)' }} />}
                      </button>
                      <button onClick={() => startEdit(prompt)} data-testid={`edit-prompt-${prompt.id}`} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-muted)' }}>
                        <Edit3 size={14} />
                      </button>
                      <button onClick={() => deletePrompt(prompt.id)} data-testid={`delete-prompt-${prompt.id}`} className="p-2 rounded-lg transition-colors hover:bg-red-500/20 text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
